import {
  deleteCoworkSession,
  listPortfolioChats,
  renameCoworkSession,
  type CoworkPortfolioChat,
} from "@/cowork/api"
import { useCoworkWorkspace } from "@/cowork/workspace-context"
import { collectionPath, useCollectionScope } from "@/cowork/scope-url"
import { useLanguage } from "@/context/language"
import { useServer } from "@/context/server"
import { useTabs, type SessionTab } from "@/context/tabs"
import { CoworkChatUnavailableError, openCoworkChat } from "@/cowork/open-chat"
import { useAppNavigate } from "@/cowork/workspace-route"
import { createMemo, createResource, For, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { Portal } from "solid-js/web"

export function CoworkChatsPage() {
  const language = useLanguage()
  const context = useCoworkWorkspace()
  const scope = useCollectionScope()
  const server = useServer()
  const tabs = useTabs()
  const navigate = useAppNavigate()
  let disposed = false
  onCleanup(() => {
    disposed = true
  })
  const [state, setState] = createStore({
    search: "",
    opening: "",
    deleting: "",
    renaming: "",
    renameChat: undefined as CoworkPortfolioChat | undefined,
    renameTitle: "",
    confirmChat: undefined as CoworkPortfolioChat | undefined,
    error: "",
  })
  const [chats, chatActions] = createResource(
    () => `${scope().companyID ?? ""}:${scope().projectID ?? ""}`,
    () =>
      listPortfolioChats({
        entityID: scope().companyID,
        projectID: scope().projectID,
      }),
  )
  const filtered = createMemo(() => {
    if (chats.loading) return []
    const query = state.search.trim().toLowerCase()
    const periodID = scope().projectID ? scope().periodID : undefined
    const ordered = (chats() ?? [])
      .filter((chat) =>
        !periodID || periodID === context.allPeriodsID || (chat.period_id ?? chat.workflow_id) === periodID,
      )
      .sort((left, right) => chatActivity(right) - chatActivity(left))
    if (!query) return ordered
    return ordered.filter((chat) =>
      `${chat.title} ${chat.scope_name} ${chat.company_name} ${chat.project_name} ${chat.period_name ?? chat.workflow_name ?? ""}`
        .toLowerCase()
        .includes(query),
    )
  })

  const openChat = async (chat: CoworkPortfolioChat) => {
    if (state.opening) return
    setState({ opening: chat.id, error: "" })
    try {
      await openCoworkChat(
        {
          companyID: chat.company_id,
          projectID: chat.project_id,
          periodID: chat.period_id ?? chat.workflow_id,
          sessionID: chat.id,
          remoteSessionID: chat.remote_opencode_session_id,
          title: chat.title,
        },
        { context, server, tabs, isCurrent: () => !disposed },
      )
    } catch (error) {
      if (!disposed)
        setState(
          "error",
          error instanceof CoworkChatUnavailableError
            ? language.t("cowork.enterprise.chats.unavailable")
            : error instanceof Error ? error.message : String(error),
        )
    } finally {
      if (!disposed) setState("opening", "")
    }
  }

  const deleteChat = async () => {
    const chat = state.confirmChat
    if (!chat || state.deleting) return
    setState({ deleting: chat.id, error: "" })
    try {
      context.selectEntity(chat.company_id)
      await deleteCoworkSession(chat.project_id, chat.id)
      await chatActions.refetch()
      setState("confirmChat", undefined)
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("deleting", "")
    }
  }

  const renameChat = async (event: SubmitEvent) => {
    event.preventDefault()
    const chat = state.renameChat
    const title = state.renameTitle.trim()
    if (!chat || !title || state.renaming) return
    setState({ renaming: chat.id, error: "" })
    try {
      context.selectEntity(chat.company_id)
      const updated = await renameCoworkSession(chat.project_id, chat.id, title)
      const tab = tabs.store.find(
        (item): item is SessionTab =>
          item.type === "session" && item.sessionId === chat.remote_opencode_session_id,
      )
      if (tab) tabs.rememberSessionTitle(tab, updated.title)
      await chatActions.refetch()
      setState({ renameChat: undefined, renameTitle: "" })
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("renaming", "")
    }
  }

  return (
    <div class="min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10">
      <div class="mx-auto max-w-[1400px]">
        <header class="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 class="text-[30px] font-semibold tracking-[-0.025em]">{language.t("cowork.enterprise.chats.title")}</h1>
            <p class="mt-1 text-[13px] text-v2-text-text-muted">{language.t("cowork.enterprise.chats.description")}</p>
          </div>
          <div class="flex gap-2">
            <input
              class="h-10 min-w-[260px] rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none placeholder:text-v2-text-text-faint"
              value={state.search}
              placeholder={language.t("cowork.enterprise.chats.search")}
              aria-label={language.t("cowork.enterprise.chats.search")}
              onInput={(event) => setState("search", event.currentTarget.value)}
            />
            <button
              type="button"
              class="h-10 rounded-lg bg-v2-background-bg-contrast px-4 text-[12px] font-semibold text-v2-text-text-contrast"
              onClick={() => {
                const projectID = scope().projectID
                if (!projectID) return navigate(collectionPath("projects", { companyID: scope().companyID }))
                const query = new URLSearchParams({ project: projectID })
                if (scope().companyID || context.state.entityId) query.set("company", scope().companyID || context.state.entityId)
                if (scope().periodID) query.set("period", scope().periodID!)
                navigate(`/new-session?${query}`)
              }}
            >
              {language.t("cowork.enterprise.newChat")}
            </button>
          </div>
        </header>
        <Show when={state.error}>
          <div class="mt-4 rounded-lg bg-v2-state-bg-danger px-4 py-3 text-[12px] text-v2-state-fg-danger">
            {state.error}
          </div>
        </Show>
        <section class="mt-7 overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base">
          <Show
            when={!chats.loading && filtered().length > 0}
            fallback={
              <div class="grid min-h-56 place-items-center p-8 text-[12px] text-v2-text-text-muted">
                {chats.loading ? language.t("common.loading") : language.t("cowork.enterprise.chats.empty")}
              </div>
            }
          >
            <div class="overflow-x-auto">
              <table class="w-full min-w-[880px] border-collapse text-left">
                <thead>
                  <tr class="border-b border-v2-border-border-base text-[10px] font-semibold uppercase tracking-[0.12em] text-v2-text-text-faint">
                    <th class="px-5 py-3">{language.t("cowork.enterprise.chats.chat")}</th>
                    <th class="px-4 py-3 text-right">{language.t("cowork.enterprise.chats.messages")}</th>
                    <th class="px-4 py-3">{language.t("cowork.enterprise.chats.lastActivity")}</th>
                    <th class="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  <For each={filtered()}>
                    {(chat) => (
                      <tr class="border-b border-v2-border-border-muted last:border-0 hover:bg-v2-overlay-simple-overlay-hover">
                        <td class="px-5 py-4">
                          <button type="button" class="text-left" onClick={() => void openChat(chat)}>
                            <span class="block text-[13px] font-medium">{chat.title}</span>
                            <span class="mt-0.5 block text-[10px] text-v2-text-text-muted">
                              {[
                                chat.project_name,
                                (chat.period_id ?? chat.workflow_id)
                                  ? chat.period_name ?? chat.workflow_name
                                  : language.t("cowork.enterprise.scope.allPeriods"),
                              ].filter(Boolean).join(" · ")}
                            </span>
                          </button>
                        </td>
                        <td class="px-4 py-4 text-right text-[12px] tabular-nums">{chat.message_count}</td>
                        <td class="px-4 py-4 text-[11px] text-v2-text-text-muted">
                          {formatDate(chat.last_message_at || chat.updated_at)}
                        </td>
                        <td class="px-5 py-4 text-right">
                          <div class="flex justify-end gap-1">
                             <button
                               type="button"
                               class="grid size-8 place-items-center rounded-md border border-v2-border-border-base hover:bg-v2-overlay-simple-overlay-hover disabled:opacity-50"
                               aria-label={language.t("cowork.enterprise.chats.continue")}
                               title={language.t("cowork.enterprise.chats.continue")}
                               disabled={!!state.opening || !!state.deleting || !!state.renaming}
                               onClick={() => void openChat(chat)}
                             >
                               <OpenIcon loading={state.opening === chat.id} />
                             </button>
                             <button
                               type="button"
                               class="grid size-8 place-items-center rounded-md border border-v2-border-border-base hover:bg-v2-overlay-simple-overlay-hover disabled:opacity-50"
                               aria-label={language.t("cowork.enterprise.chats.rename")}
                               title={language.t("cowork.enterprise.chats.rename")}
                               disabled={!!state.opening || !!state.deleting || !!state.renaming}
                               onClick={() => setState({ renameChat: chat, renameTitle: chat.title, error: "" })}
                             >
                               <RenameIcon />
                            </button>
                            <button
                              type="button"
                              class="grid size-8 place-items-center rounded-md text-v2-state-fg-danger hover:bg-v2-state-bg-danger disabled:opacity-50"
                              aria-label={language.t("cowork.enterprise.chats.delete")}
                              title={language.t("cowork.enterprise.chats.delete")}
                               disabled={!!state.opening || !!state.deleting || !!state.renaming}
                              onClick={() => setState("confirmChat", chat)}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
        </section>
      </div>
      <Show when={state.renameChat}>
        <Portal>
          <div
            class="fixed inset-0 z-[110] flex items-center justify-center bg-v2-overlay-simple-overlay-scrim p-4"
            onClick={() => !state.renaming && setState({ renameChat: undefined, renameTitle: "" })}
          >
            <section
              class="w-full max-w-md rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-5 shadow-[var(--v2-elevation-raised)]"
              role="dialog"
              aria-modal="true"
              aria-labelledby="rename-chat-title"
              onClick={(event) => event.stopPropagation()}
            >
              <form onSubmit={renameChat}>
                <h2 id="rename-chat-title" class="text-[16px] font-semibold">
                  {language.t("cowork.enterprise.chats.renameTitle")}
                </h2>
                <p class="mt-2 text-[12px] leading-5 text-v2-text-text-muted">
                  {language.t("cowork.enterprise.chats.renameDescription")}
                </p>
                <input
                  ref={(element) => queueMicrotask(() => element.select())}
                  class="mt-4 h-11 w-full rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none focus:border-v2-border-border-focus"
                  value={state.renameTitle}
                  maxlength="255"
                  required
                  aria-label={language.t("cowork.enterprise.chats.renameLabel")}
                  onInput={(event) => setState("renameTitle", event.currentTarget.value)}
                />
                <Show when={state.error}>
                  <p class="mt-3 rounded-lg bg-v2-state-bg-danger px-3 py-2 text-[11px] text-v2-state-fg-danger">
                    {state.error}
                  </p>
                </Show>
                <div class="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    class="rounded-md border border-v2-border-border-base px-4 py-2 text-[12px]"
                    disabled={!!state.renaming}
                    onClick={() => setState({ renameChat: undefined, renameTitle: "", error: "" })}
                  >
                    {language.t("common.cancel")}
                  </button>
                  <button
                    type="submit"
                    class="rounded-md bg-v2-background-bg-contrast px-4 py-2 text-[12px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
                    disabled={!!state.renaming || !state.renameTitle.trim()}
                  >
                    {state.renaming
                      ? language.t("cowork.enterprise.chats.renaming")
                      : language.t("cowork.enterprise.chats.saveName")}
                  </button>
                </div>
              </form>
            </section>
          </div>
        </Portal>
      </Show>
      <Show when={state.confirmChat}>
        {(chat) => (
          <Portal>
            <div
              class="fixed inset-0 z-[110] flex items-center justify-center bg-v2-overlay-simple-overlay-scrim p-4"
              onClick={() => !state.deleting && setState("confirmChat", undefined)}
            >
              <section
                class="w-full max-w-md rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-5 shadow-[var(--v2-elevation-raised)]"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="delete-chat-title"
                onClick={(event) => event.stopPropagation()}
              >
                <h2 id="delete-chat-title" class="text-[16px] font-semibold">
                  {language.t("cowork.enterprise.chats.deleteTitle")}
                </h2>
                <p class="mt-2 text-[12px] leading-5 text-v2-text-text-muted">
                  {language.t("cowork.enterprise.chats.deleteDescription", { chat: chat().title })}
                </p>
                <div class="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    class="rounded-md border border-v2-border-border-base px-4 py-2 text-[12px]"
                    disabled={!!state.deleting}
                    onClick={() => setState("confirmChat", undefined)}
                  >
                    {language.t("common.cancel")}
                  </button>
                  <button
                    type="button"
                    class="rounded-md bg-v2-state-fg-danger px-4 py-2 text-[12px] font-semibold text-white disabled:opacity-50"
                    disabled={!!state.deleting}
                    onClick={() => void deleteChat()}
                  >
                    {state.deleting
                      ? language.t("cowork.enterprise.chats.deleting")
                      : language.t("cowork.enterprise.chats.confirmDelete")}
                  </button>
                </div>
              </section>
            </div>
          </Portal>
        )}
      </Show>
    </div>
  )
}

function OpenIcon(props: { loading: boolean }) {
  if (props.loading) {
    return (
      <span class="size-4 animate-spin rounded-full border-2 border-v2-border-border-base border-t-v2-icon-icon-base" />
    )
  }
  return (
    <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
      <path d="M14 4h6v6M20 4l-9 9" />
      <path d="M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6" />
    </svg>
  )
}

function RenameIcon() {
  return (
    <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
      <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="m16.5 3.5 4 4L12 16l-4 1 1-4z" />
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
      <path d="M4 7h16M9 7V4h6v3M7 7l1 14h8l1-14M10 11v6M14 11v6" />
    </svg>
  )
}

function chatActivity(chat: CoworkPortfolioChat) {
  const timestamp = Date.parse(chat.last_message_at || chat.updated_at || chat.created_at)
  return Number.isFinite(timestamp) ? timestamp : 0
}

function formatDate(value: string) {
  const date = new Date(value)
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date)
    : ""
}
